import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppointments, useSlotBlocks, usePublicAppSettings, useEffectiveSlotConfig, type AppointmentRow as ApptRow } from "@/lib/queries";
import { useReorderAppointment, useDayReorderAppointment } from "@/lib/mutations";
import { AppointmentRow } from "@/features/appointments/AppointmentRow";
import { BookDialog } from "@/features/appointments/BookDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { Has } from "@/components/Has";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { isSlotBlocked, calendarDayStatus } from "@emr/shared/scheduling";
import type { WeeklySchedule, SlotDef } from "@emr/shared/scheduling";
import { fmtDate, todayIST } from "@emr/shared/india";
import { Plus, Search } from "lucide-react";

type TabKey = "checkedin" | "complete" | "all";

const TAB_STORAGE_KEY = "emr:appointments:view";

/**
 * Persisted view shape (Phase 3, 2026-10-18): tabs are keyed by SLOT KEY
 * (any key of the date's effective config — 'Morning'/'Evening' for v1),
 * so N-slot configs persist independently per slot. openAccordions holds
 * slot keys too (default: all open).
 */
interface PersistedView {
  tabs: Record<string, TabKey>;
  openAccordions: string[];
}

function isValidTab(v: unknown): v is TabKey {
  return v === "checkedin" || v === "complete" || v === "all";
}

/**
 * Read the persisted view, migrating every legacy shape:
 *   1. CURRENT: { tabs: Record<slotKey, TabKey>, openAccordions }
 *   2. 2026-10-13 shape: { tabs: { morning, evening }, openAccordions } —
 *      mapped onto the same-named config keys ('Morning'/'Evening').
 *   3. Pre-2026-10-13 shape: { tab, openAccordions } (single shared tab) —
 *      mapped onto both legacy keys.
 * Legacy shapes stored LOWERCASE accordion values ('morning'/'evening') and
 * lowercase tab keys — both are mapped onto the v1 config keys so the
 * migration is seamless for existing users. Unknown slot keys in the tabs
 * map are kept as-is (a config key that returns later still finds its tab);
 * the consumer defaults missing keys to 'checkedin'.
 */
function readPersistedView(): PersistedView | null {
  try {
    const raw = localStorage.getItem(TAB_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedView> & {
      tab?: unknown;
      tabs?: Record<string, unknown> & { morning?: unknown; evening?: unknown };
    };
    if (!Array.isArray(parsed.openAccordions)) return null;
    const tabs: Record<string, TabKey> = {};
    if (parsed.tabs && typeof parsed.tabs === "object" && !Array.isArray(parsed.tabs)) {
      // Copy CURRENT-shape entries — skipping the legacy lowercase keys,
      // which are mapped below (a current-shape value always wins over the
      // stale legacy one; without this the legacy mapping would overwrite
      // every tab change back to the pre-migration value on reload).
      for (const [k, v] of Object.entries(parsed.tabs)) {
        if (k === "morning" || k === "evening") continue;
        if (isValidTab(v)) tabs[k] = v;
      }
    }
    // Legacy per-slot shape stored lowercase keys; map them onto the v1
    // config keys — ONLY when the canonical key isn't already present.
    if (isValidTab(parsed.tabs?.morning) && tabs.Morning === undefined) tabs.Morning = parsed.tabs.morning;
    if (isValidTab(parsed.tabs?.evening) && tabs.Evening === undefined) tabs.Evening = parsed.tabs.evening;
    // Oldest shape: one shared tab for both slots.
    if (isValidTab(parsed.tab)) {
      tabs.Morning = parsed.tab;
      tabs.Evening = parsed.tab;
    }
    // openAccordions: map legacy lowercase values onto the v1 config keys;
    // drop anything that isn't a string.
    const LEGACY_KEY_MAP: Record<string, string> = { morning: "Morning", evening: "Evening" };
    const openAccordions = parsed.openAccordions
      .filter((k): k is string => typeof k === "string")
      .map((k) => LEGACY_KEY_MAP[k] ?? k);
    return { tabs, openAccordions };
  } catch {
    return null;
  }
}

function writePersistedView(v: PersistedView) {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(v));
  } catch {
    // Non-fatal — localStorage may be unavailable (private mode, quota).
  }
}

function filterByTab(rows: ApptRow[], tab: TabKey) {
  if (tab === "checkedin") return rows.filter((r) => r.status === "CheckedIn");
  if (tab === "complete") return rows.filter((r) => r.status === "Completed");
  // All: every confirmed appointment (Booked + CheckedIn + Completed).
  // Pending status is excluded — pending appointments live on the separate
  // Pending Appointments page (/pending-appointments), not here.
  return rows.filter((r) => r.status === "Booked" || r.status === "CheckedIn" || r.status === "Completed");
}

function QueueList({ slotKey, appts, date, blocks, weeklySchedule }: { slotKey: string; appts: ApptRow[]; date: string; blocks: any[]; weeklySchedule?: WeeklySchedule }) {
  const reorder = useReorderAppointment();
  const dayReorder = useDayReorderAppointment();
  const dObj = new Date(date + "T00:00:00");
  // Day-level status first: off weekdays and whole-day blocks render a
  // "closed" banner instead of the misleading "No appointments".
  const dayStatus = calendarDayStatus(dObj, weeklySchedule as WeeklySchedule, blocks as any);
  if (dayStatus.kind === "off") {
    return (
      <div className="rounded-lg bg-slate-100 p-4 text-sm text-slate-600 dark:bg-dark-700 dark:text-dark-200">
        Clinic closed — {dObj.toLocaleDateString("en-IN", { weekday: "long" })} is a non-working day (Settings → Slot Config).
      </div>
    );
  }
  if (dayStatus.kind === "blocked") {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">Day blocked (Settings → Block Days)</div>
    );
  }
  // Per-slot blocked banner — generic key. The block REASON is admin-only at
  // the API layer (non-admin rows carry reason:null), so it is never rendered
  // here; admins who need the reason read it in Settings → Block Days.
  if (isSlotBlocked(dObj, slotKey, blocks as any)) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">Slot blocked (Settings → Block Days)</div>
    );
  }

  if (appts.length === 0) {
    return <div className="p-4 text-center text-sm text-muted">No appointments</div>;
  }

  return (
    <div>
      {appts.map((a) => (
        <AppointmentRow
          key={a.id}
          appt={a}
          daySerial={a.daySerial}
          onReorder={(id, dir) => {
            // CheckedIn rows reorder by DAY SERIAL (cross-slot, admin-only
            // endpoint); Booked/Pending rows keep the within-slot queueOrder
            // swap (the old endpoint 422s checked-in rows since Phase 2).
            if (a.status === "CheckedIn") dayReorder.mutate({ id, direction: dir });
            else reorder.mutate({ id, direction: dir });
          }}
        />
      ))}
    </div>
  );
}

/**
 * One accordion section per slot of the date's effective config. Renders the
 * three Checked In / Complete / All tabs inside the accordion content, driven
 * by this slot's own tab state — switching tabs here never affects the other
 * slots' accordions. Per-slot counts are shown on each tab trigger.
 */
function SlotAccordion({
  value,
  def,
  allRows,
  date,
  blocks,
  weeklySchedule,
  tab,
  onTabChange,
}: {
  value: string;
  def: SlotDef;
  allRows: ApptRow[];
  date: string;
  blocks: any[];
  weeklySchedule?: WeeklySchedule;
  tab: TabKey;
  onTabChange: (t: TabKey) => void;
}) {
  const checkedInCount = useMemo(() => allRows.filter((r) => r.status === "CheckedIn").length, [allRows]);
  const completeCount = useMemo(() => allRows.filter((r) => r.status === "Completed").length, [allRows]);
  const filtered = useMemo(() => filterByTab(allRows, tab), [allRows, tab]);

  return (
    <AccordionItem value={value} className="border-cream-200 dark:border-dark-700">
      <AccordionTrigger className="px-4 text-maroon-700 dark:text-amber-500 hover:no-underline">
        <span className="flex items-center gap-2">
          {def.label} ({filtered.length})
          <span className="text-xs font-normal text-muted">{def.startTime}–{def.endTime}</span>
        </span>
      </AccordionTrigger>
      <AccordionContent className="p-0">
        <div className="px-4 pt-2 pb-1">
          <Tabs value={tab} onValueChange={(v) => onTabChange(v as TabKey)}>
            <TabsList>
              <TabsTrigger value="checkedin">Checked In ({checkedInCount})</TabsTrigger>
              <TabsTrigger value="complete">Complete ({completeCount})</TabsTrigger>
              <TabsTrigger value="all">All ({allRows.length})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <QueueList slotKey={def.key} appts={filtered} date={date} blocks={blocks} weeklySchedule={weeklySchedule} />
      </AccordionContent>
    </AccordionItem>
  );
}

/**
 * Patient search bar — sits on the same line as the CardTitle and the date
 * input in the CardHeader. Filters the day's appointment rows by patient name
 * or clinic UHID (case-insensitive). Clicking a result navigates to the EMR
 * visit page when the appointment has a linked visit (and the user has
 * emr.view), otherwise to the patient detail page. Mirrors AppointmentRow's
 * navigation logic.
 */
function PatientSearchBar({ rows }: { rows: ApptRow[] }) {
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const canViewEmr = isAdmin || permissions.has("emr.view");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return rows
      .filter(
        (r) =>
          (r.patientId || r.visitId) &&
          ((r.patientName ?? "").toLowerCase().includes(q) ||
            (r.clinicUhid ?? "").toLowerCase().includes(q)),
      )
      .slice(0, 10);
  }, [rows, query]);

  const go = (r: ApptRow) => {
    if (r.visitId && canViewEmr) navigate(`/emr/visits/${r.visitId}`);
    else if (r.patientId) navigate(`/patients/${r.patientId}`);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative w-full sm:w-72">
      <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
      <Popover open={open && results.length > 0} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              if (query.trim()) setOpen(true);
            }}
            placeholder="Search patient by name or UHID…"
            className="h-9 w-full pl-8"
            aria-label="Search patient by name or UHID"
          />
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="w-72 p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="max-h-72 overflow-y-auto py-1">
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => go(r)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700"
              >
                <span className="min-w-0">
                  <span className={cn("block truncate text-ink dark:text-cream-50", r.isNewPatient ? "font-bold" : "font-medium")}>
                    {r.patientName ?? "Unknown"}
                  </span>
                  <span className="block truncate text-xs text-muted">
                    {r.clinicUhid ?? "—"}
                    {r.slot ? ` · ${r.slot}` : ""}
                    {r.status ? ` · ${r.status}` : ""}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function Appointments() {
  // todayIST() formats in Asia/Kolkata so the default date matches the
  // user's local day instead of the UTC day (which is yesterday before 5:30 AM IST).
  const today = todayIST();
  const [date, setDate] = useState(today);
  const { data, isLoading } = useAppointments(date);
  const { data: blocksData } = useSlotBlocks();
  const { data: settingsData } = usePublicAppSettings();
  const blocks = blocksData?.rows ?? [];
  const weeklySchedule = settingsData?.weeklySchedule as WeeklySchedule | undefined;
  const [bookOpen, setBookOpen] = useState(false);

  // The date's effective slot config (graceful fallback to legacy
  // Morning/Evening defs from settings when the endpoint is unavailable).
  const { slotList } = useEffectiveSlotConfig(date, settingsData);

  // Persisted view: per-slot tabs + open accordions, restored from localStorage.
  // Tabs are keyed by SLOT KEY — every slot of the config switches its status
  // tab independently. Missing keys default to 'checkedin'.
  const [tabs, setTabs] = useState<Record<string, TabKey>>(
    () => readPersistedView()?.tabs ?? {},
  );
  const [openAccordions, setOpenAccordions] = useState<string[]>(
    () => readPersistedView()?.openAccordions ?? [],
  );

  // Default open accordions: ALL of the current config's slot keys (once the
  // config resolves). A persisted list wins — but only keys that still exist
  // in the config are kept, so a config change cleanly drops stale keys.
  const effectiveOpenAccordions = useMemo(() => {
    const keys = slotList.map((s) => s.key);
    if (openAccordions.length > 0) return openAccordions.filter((k) => keys.includes(k));
    return keys;
  }, [openAccordions, slotList]);

  // Persist on change (the new keyed-by-slot-key shape).
  useEffect(() => {
    writePersistedView({ tabs, openAccordions: effectiveOpenAccordions });
  }, [tabs, effectiveOpenAccordions]);

  const rows = data?.rows ?? [];
  // Top-level filter: exclude Cancelled, NoShow, AND Pending from the
  // Appointments page. Pending appointments belong on the separate
  // Pending Appointments page (/pending-appointments), not here.
  const activeRows = useMemo(
    () => rows.filter((r) => r.status !== "Cancelled" && r.status !== "NoShow" && r.status !== "Pending"),
    [rows],
  );

  // Rows grouped per config slot key; rows whose slot is NOT in the config
  // (data drift — e.g. a config change remapped keys) render in a final
  // "Unassigned" accordion, only when non-empty.
  const rowsBySlot = useMemo(() => {
    const map = new Map<string, ApptRow[]>();
    for (const def of slotList) map.set(def.key, []);
    const unassigned: ApptRow[] = [];
    for (const r of activeRows) {
      const arr = map.get(r.slot ?? "");
      if (arr) arr.push(r);
      else unassigned.push(r);
    }
    return { map, unassigned };
  }, [activeRows, slotList]);

  const isToday = date === today;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Appointments</h1>
        <div className="flex items-center gap-3">
          <Has perm="appointments.create">
            <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => setBookOpen(true)}>
              <Plus size={14} className="mr-1" /> Book appointment
            </Button>
          </Has>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-maroon-700 dark:text-amber-500">{isToday ? "Today's appointments" : `Appointments — ${fmtDate(date)}`}</CardTitle>
            <div className="flex items-center gap-2">
              <PatientSearchBar rows={rows} />
              <label htmlFor="appt-date" className="text-sm text-muted">Date</label>
              <Input
                id="appt-date"
                type="date"
                value={date}
                max={today}
                onChange={(e) => setDate(e.target.value)}
                className="w-40"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <Accordion type="multiple" value={effectiveOpenAccordions} onValueChange={setOpenAccordions} className="w-full">
              {slotList.map((def) => (
                <SlotAccordion
                  key={def.key}
                  value={def.key}
                  def={def}
                  allRows={rowsBySlot.map.get(def.key) ?? []}
                  date={date}
                  blocks={blocks}
                  weeklySchedule={weeklySchedule}
                  tab={tabs[def.key] ?? "checkedin"}
                  onTabChange={(t) => setTabs((prev) => ({ ...prev, [def.key]: t }))}
                />
              ))}
              {rowsBySlot.unassigned.length > 0 && (
                <SlotAccordion
                  value="__unassigned"
                  def={{ key: "__unassigned", label: "Unassigned", startTime: "", endTime: "", checkinStart: "", checkinEnd: "", maxAppts: 0, sortOrder: slotList.length + 1 }}
                  allRows={rowsBySlot.unassigned}
                  date={date}
                  blocks={blocks}
                  weeklySchedule={weeklySchedule}
                  tab={tabs["__unassigned"] ?? "all"}
                  onTabChange={(t) => setTabs((prev) => ({ ...prev, __unassigned: t }))}
                />
              )}
            </Accordion>
          )}
        </CardContent>
      </Card>

      <BookDialog open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}
